import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Job, CompletedJob, WeekPayment, ChildJobsSummary } from '@family/shared';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useJobs() {
  return useQuery<{ data: Job[] }>({
    queryKey: ['jobs'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/jobs`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch jobs');
      return res.json();
    },
    staleTime: 10 * 60 * 1000, // Jobs list rarely changes
  });
}

export function useCompletedJobs(childName: string, weekStart: string) {
  return useQuery<{ data: CompletedJob[] }>({
    queryKey: ['jobs', 'completed', childName, weekStart],
    queryFn: async () => {
      const res = await fetch(
        `${API_URL}/jobs/completed?childName=${encodeURIComponent(childName)}&weekStart=${weekStart}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Failed to fetch completed jobs');
      return res.json();
    },
  });
}

export function useWeekPayment(childName: string, weekStart: string) {
  return useQuery<{ data: WeekPayment | null }>({
    queryKey: ['jobs', 'week-payment', childName, weekStart],
    queryFn: async () => {
      const res = await fetch(
        `${API_URL}/jobs/week-payment?childName=${encodeURIComponent(childName)}&weekStart=${weekStart}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Failed to fetch week payment');
      return res.json();
    },
  });
}

export function useJobsSummary() {
  return useQuery<{ data: ChildJobsSummary[] }>({
    queryKey: ['jobs', 'summary'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/jobs/summary`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch jobs summary');
      return res.json();
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useRecordJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      jobId: string;
      childName: string;
      completedDate: string;
      approved: boolean;
    }) => {
      const res = await fetch(`${API_URL}/jobs/completed`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to record job');
      }
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['jobs', 'completed', variables.childName] });
      queryClient.invalidateQueries({ queryKey: ['jobs', 'unpaid-weeks', variables.childName] });
      queryClient.invalidateQueries({ queryKey: ['jobs', 'summary'] });
    },
  });
}

export function useApproveJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { id: string; approved: boolean; childName: string }) => {
      const res = await fetch(`${API_URL}/jobs/completed/${params.id}/approve`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved: params.approved }),
      });
      if (!res.ok) throw new Error('Failed to update job approval');
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['jobs', 'completed', variables.childName] });
      queryClient.invalidateQueries({ queryKey: ['jobs', 'unpaid-weeks', variables.childName] });
      queryClient.invalidateQueries({ queryKey: ['jobs', 'summary'] });
    },
  });
}

export function useDeleteCompletedJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { id: string; childName: string }) => {
      const res = await fetch(`${API_URL}/jobs/completed/${params.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to delete completed job');
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['jobs', 'completed', variables.childName] });
      queryClient.invalidateQueries({ queryKey: ['jobs', 'unpaid-weeks', variables.childName] });
      queryClient.invalidateQueries({ queryKey: ['jobs', 'summary'] });
    },
  });
}

export function useUnpaidWeeks(childName: string) {
  return useQuery<{ data: { weekStart: string; earnedPence: number }[] }>({
    queryKey: ['jobs', 'unpaid-weeks', childName],
    queryFn: async () => {
      const res = await fetch(
        `${API_URL}/jobs/unpaid-weeks?childName=${encodeURIComponent(childName)}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Failed to fetch unpaid weeks');
      return res.json();
    },
  });
}

export function useMarkAllWeeksPaid() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { childName: string }) => {
      const res = await fetch(`${API_URL}/jobs/mark-all-weeks-paid`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to mark weeks as paid');
      }
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['jobs', 'week-payment', variables.childName] });
      queryClient.invalidateQueries({ queryKey: ['jobs', 'unpaid-weeks', variables.childName] });
      queryClient.invalidateQueries({ queryKey: ['jobs', 'summary'] });
    },
  });
}

export function useMarkWeekPaid() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { childName: string; weekStart: string }) => {
      const res = await fetch(`${API_URL}/jobs/week-payment`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to mark week as paid');
      }
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['jobs', 'week-payment', variables.childName] });
      queryClient.invalidateQueries({ queryKey: ['jobs', 'summary'] });
    },
  });
}
